// v0.20.14 — full-screen QR scanner overlay.
//
// Live camera (getUserMedia) + jsQR decode loop. Works in Safari, Chrome and
// Firefox on iOS 14.3+. In-app browsers (which block the camera) are handled
// upstream by the entry-stage browser gate, so here a camera failure just shows
// a short message + Close — no photo fallback.

import jsQR from "jsqr";

interface ScannerOpts {
  /** Called with each decoded string. Return true to accept & close, false to keep scanning (e.g. not an M code). */
  onResult: (text: string) => boolean;
  onClose: () => void;
}

const SHARE_ICON = ""; // (reticle is CSS; no icon needed here)

export function openScanner({ onResult, onClose }: ScannerOpts): void {
  const root = document.createElement("div");
  root.className = "scanner";
  root.innerHTML = `
    <video class="scanner-feed" playsinline muted></video>
    <div class="scanner-shade"></div>
    <button class="scanner-x" type="button" aria-label="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>
    <div class="scanner-top"><h3>Scan to pay</h3></div>
    <div class="scanner-mid">
      <div class="reticle">
        <span class="cn tl"></span><span class="cn tr"></span>
        <span class="cn bl"></span><span class="cn br"></span>
        <span class="scanline"></span>
      </div>
    </div>
    <div class="scanner-msg" id="scanMsg" hidden></div>
  `;
  void SHARE_ICON;
  document.body.appendChild(root);

  const video = root.querySelector<HTMLVideoElement>(".scanner-feed")!;
  const msg = root.querySelector<HTMLElement>("#scanMsg")!;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true });

  let stream: MediaStream | null = null;
  let raf = 0;
  let closed = false;
  let cooldownUntil = 0; // brief pause after a non-M code so the toast is readable

  function teardown() {
    if (closed) return;
    closed = true;
    cancelAnimationFrame(raf);
    stream?.getTracks().forEach((t) => t.stop());
    root.remove();
  }

  function close() {
    teardown();
    onClose();
  }

  function flash(text: string) {
    msg.textContent = text;
    msg.hidden = false;
    cooldownUntil = Date.now() + 1600;
    window.setTimeout(() => {
      if (!closed) msg.hidden = true;
    }, 1600);
  }

  root.querySelector<HTMLButtonElement>(".scanner-x")!.addEventListener("click", close);

  function tick() {
    if (closed) return;
    raf = requestAnimationFrame(tick);
    if (Date.now() < cooldownUntil) return;
    if (video.readyState !== video.HAVE_ENOUGH_DATA || !ctx) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;
    canvas.width = w;
    canvas.height = h;
    ctx.drawImage(video, 0, 0, w, h);
    const img = ctx.getImageData(0, 0, w, h);
    const code = jsQR(img.data, w, h, { inversionAttempts: "dontInvert" });
    if (!code || !code.data) return;
    const accepted = onResult(code.data);
    if (accepted) teardown();
    else flash("That's not an M\u00AE code.");
  }

  async function start() {
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      video.srcObject = stream;
      await video.play();
      raf = requestAnimationFrame(tick);
    } catch {
      // Real-browser permission denial / no camera. (In-app webviews are gated
      // upstream.) Show a short message; user can close and retry.
      root.querySelector<HTMLElement>(".scanner-mid")!.hidden = true;
      msg.hidden = false;
      msg.textContent = "Camera access is needed to scan. Enable it and try again.";
    }
  }

  void start();
}
